import React, { useEffect, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import GoogleIcon from '../../../icon/GoogleIcon'
import { MUI_DATE_PICKER_ES_LOCALE_TEXT } from '../../../components/constants/muiDatePickerEsLocaleText'
import dayjs from '../../../utils/dayjsConfig'
import 'dayjs/locale/es'
import {
  getEffectivenessReportDateActionWrapSx,
  getEffectivenessReportDateButtonSx,
  getEffectivenessReportDatePickerSlotPropsBase,
  getEffectivenessReportDateStackSx,
  getEffectivenessReportDateTextFieldSx,
  REPORT_DATE_RANGE_LABEL_SX,
  REPORT_DATE_RANGE_ROW_PICKER_WIDTH,
  REPORT_DATE_RANGE_SEPARATOR_SX
} from './styles/effectivenessReportDateRangeSearchSx'

dayjs.locale('es')

function parseDateValue(dateStr) {
  if (!dateStr) return null
  const d = dayjs(dateStr, 'YYYY-MM-DD', true)
  return d.isValid() ? d : null
}

export default function EffectivenessReportDateRangeSearch({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  maxDate,
  onSearch,
  direction = 'row',
  applyDatesOnSearchOnly = false
}) {
  const isColumn = direction === 'column'

  const [draftStart, setDraftStart] = useState(startDate)
  const [draftEnd, setDraftEnd] = useState(endDate)

  useEffect(() => {
    if (!applyDatesOnSearchOnly) return
    setDraftStart(startDate)
    setDraftEnd(endDate)
  }, [applyDatesOnSearchOnly, startDate, endDate])

  const effectiveStart = applyDatesOnSearchOnly ? draftStart : startDate
  const effectiveEnd = applyDatesOnSearchOnly ? draftEnd : endDate

  const handleStartChange = (v) => {
    const next = v && v.isValid() ? v.format('YYYY-MM-DD') : null
    if (applyDatesOnSearchOnly) setDraftStart(next)
    else setStartDate(next)
  }

  const handleEndChange = (v) => {
    const next = v && v.isValid() ? v.format('YYYY-MM-DD') : null
    if (applyDatesOnSearchOnly) setDraftEnd(next)
    else setEndDate(next)
  }

  const handleSearchClick = () => {
    if (applyDatesOnSearchOnly) {
      onSearch?.(draftStart, draftEnd)
      return
    }
    onSearch?.()
  }
  const maxD = parseDateValue(maxDate) ?? dayjs()
  const startD = parseDateValue(effectiveStart)
  const rangeMaxD = startD?.add(12, 'month')
  const endMaxD = rangeMaxD && rangeMaxD.isBefore(maxD, 'day') ? rangeMaxD : maxD
  const textFieldSx = getEffectivenessReportDateTextFieldSx({
    isColumn,
    rowPickerWidth: REPORT_DATE_RANGE_ROW_PICKER_WIDTH
  })
  const pickerSlotPropsBase = getEffectivenessReportDatePickerSlotPropsBase({
    isColumn,
    textFieldSx
  })

  const startSlotProps = {
    ...pickerSlotPropsBase,
    textField: {
      ...pickerSlotPropsBase.textField,
      ...(!isColumn ? { inputProps: { 'aria-label': 'Fecha desde' } } : {})
    }
  }

  const endSlotProps = {
    ...pickerSlotPropsBase,
    textField: {
      ...pickerSlotPropsBase.textField,
      ...(!isColumn ? { inputProps: { 'aria-label': 'Fecha hasta' } } : {})
    }
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale="es"
      localeText={MUI_DATE_PICKER_ES_LOCALE_TEXT}
    >
      <Stack
        direction={direction}
        spacing={isColumn ? 2 : 0}
        sx={getEffectivenessReportDateStackSx(isColumn)}
      >
        {!isColumn && (
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            sx={REPORT_DATE_RANGE_LABEL_SX}
          >
            Desde
          </Typography>
        )}
        <DatePicker
          label={isColumn ? 'Desde' : undefined}
          value={parseDateValue(effectiveStart)}
          onChange={handleStartChange}
          maxDate={maxD}
          slotProps={startSlotProps}
          sx={isColumn ? undefined : { width: REPORT_DATE_RANGE_ROW_PICKER_WIDTH }}
        />
        {!isColumn && <Box component="span" aria-hidden sx={REPORT_DATE_RANGE_SEPARATOR_SX} />}
        {!isColumn && (
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            sx={REPORT_DATE_RANGE_LABEL_SX}
          >
            Hasta
          </Typography>
        )}
        <DatePicker
          label={isColumn ? 'Hasta' : undefined}
          value={parseDateValue(effectiveEnd)}
          onChange={handleEndChange}
          minDate={startD ?? undefined}
          maxDate={endMaxD}
          slotProps={endSlotProps}
          sx={isColumn ? undefined : { width: REPORT_DATE_RANGE_ROW_PICKER_WIDTH }}
        />
        <Box sx={getEffectivenessReportDateActionWrapSx(isColumn)}>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSearchClick}
            fullWidth={isColumn}
            startIcon={<GoogleIcon name="search" size={18} color="inherit" />}
            sx={getEffectivenessReportDateButtonSx(isColumn)}
          >
            Buscar
          </Button>
        </Box>
      </Stack>
    </LocalizationProvider>
  )
}
